import React, { useMemo } from 'react';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { fmtMoney, fmtPct } from '../utils/format';

const num = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
};

/**
 * Editor for a single P&L section (array of line items).
 * Auto-computes percent against `salesBase` (total direct income).
 */
const Section = ({ title, items, onChange, salesBase, accent }) => {
    const totalAmount = useMemo(
        () => items.reduce((s, it) => s + num(it.amount), 0),
        [items]
    );

    const update = (idx, patch) => {
        const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
        onChange(next);
    };

    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
    const add = () => onChange([...items, { name: '', amount: 0, percent: 0 }]);

    return (
        <div className={`fr-section ${accent || ''}`}>
            <div className="fr-section-header">
                <h3>{title}</h3>
                <button type="button" className="fr-btn-tiny" onClick={add}>
                    <HiOutlinePlus size={14} /> Add line
                </button>
            </div>

            <table className="fr-edit-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th className="fr-num">Amount</th>
                        <th className="fr-num">% of Sales</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td colSpan={4} className="fr-empty-row">No line items. Click "Add line" or use Auto-fill.</td></tr>
                    ) : items.map((it, idx) => {
                        const computedPct = salesBase > 0 ? (num(it.amount) / salesBase) * 100 : 0;
                        return (
                            <tr key={idx}>
                                <td>
                                    <input
                                        type="text"
                                        value={it.name}
                                        onChange={e => update(idx, { name: e.target.value })}
                                        placeholder="Line item name"
                                    />
                                </td>
                                <td className="fr-num">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={it.amount}
                                        onChange={e => update(idx, { amount: num(e.target.value), percent: salesBase > 0 ? (num(e.target.value) / salesBase) * 100 : 0 })}
                                    />
                                </td>
                                <td className="fr-num">
                                    <input
                                        type="number"
                                        step="0.001"
                                        value={it.percent ?? computedPct}
                                        onChange={e => update(idx, { percent: num(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <button type="button" className="fr-icon-btn fr-icon-danger" title="Remove" onClick={() => remove(idx)}>
                                        <HiOutlineTrash size={14} />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td><strong>Section total</strong></td>
                        <td className="fr-num"><strong>{fmtMoney(totalAmount, { currency: '' })}</strong></td>
                        <td className="fr-num"><strong>{salesBase > 0 ? fmtPct((totalAmount / salesBase) * 100) : '—'}</strong></td>
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

const TotalRow = ({ label, value, onChange, salesBase, kind }) => {
    const computedPct = salesBase > 0 ? (num(value?.amount) / salesBase) * 100 : 0;
    return (
        <div className={`fr-total-row fr-total-${kind || 'neutral'}`}>
            <div className="fr-total-label">{label}</div>
            <input
                type="number"
                step="0.01"
                value={value?.amount ?? 0}
                onChange={e => onChange({ amount: num(e.target.value), percent: salesBase > 0 ? (num(e.target.value) / salesBase) * 100 : 0 })}
                className="fr-total-amount"
            />
            <input
                type="number"
                step="0.001"
                value={value?.percent ?? computedPct}
                onChange={e => onChange({ ...value, percent: num(e.target.value) })}
                className="fr-total-pct"
            />
        </div>
    );
};

const PnLEditor = ({ draft, onChange }) => {
    const setField = (field, value) => onChange({ ...draft, [field]: value });

    const salesBase = num(draft.totalDirectIncome?.amount)
        || draft.directIncome.reduce((s, it) => s + num(it.amount), 0);

    // Auto-recompute totals based on line items (helps when user edits a line manually)
    const recomputeTotals = () => {
        const sumDI  = draft.directIncome.reduce((s, it) => s + num(it.amount), 0);
        const sumDE  = draft.directExpenses.reduce((s, it) => s + num(it.amount), 0);
        const sumIE  = draft.indirectExpenses.reduce((s, it) => s + num(it.amount), 0);
        const sumII  = draft.indirectIncome.reduce((s, it) => s + num(it.amount), 0);
        const gross  = sumDI - sumDE;
        const netOp  = gross - sumIE;
        const net    = netOp + sumII;
        const base   = sumDI > 0 ? sumDI : 1;
        onChange({
            ...draft,
            totalDirectIncome: { amount: sumDI, percent: 100 },
            totalDirectExpenses: { amount: sumDE, percent: (sumDE / base) * 100 },
            grossProfitLoss:    { amount: gross, percent: (gross / base) * 100 },
            totalIndirectExpenses: { amount: sumIE, percent: (sumIE / base) * 100 },
            totalExpense:       { amount: sumIE, percent: (sumIE / base) * 100 },
            netOperatingProfitLoss: { amount: netOp, percent: (netOp / base) * 100 },
            totalIndirectIncome: { amount: sumII, percent: (sumII / base) * 100 },
            netProfitLoss:      { amount: net, percent: (net / base) * 100 }
        });
    };

    return (
        <div className="fr-pnl-editor">
            <Section
                title="Direct Income"
                accent="fr-income"
                items={draft.directIncome}
                onChange={(items) => setField('directIncome', items)}
                salesBase={salesBase}
            />
            <TotalRow
                label="Total Direct Income"
                value={draft.totalDirectIncome}
                onChange={(v) => setField('totalDirectIncome', v)}
                salesBase={salesBase}
                kind="income"
            />

            <Section
                title="Direct Expenses"
                accent="fr-expense"
                items={draft.directExpenses}
                onChange={(items) => setField('directExpenses', items)}
                salesBase={salesBase}
            />
            <TotalRow
                label="Total Direct Expenses"
                value={draft.totalDirectExpenses}
                onChange={(v) => setField('totalDirectExpenses', v)}
                salesBase={salesBase}
                kind="expense"
            />
            <TotalRow
                label="GROSS PROFIT / LOSS"
                value={draft.grossProfitLoss}
                onChange={(v) => setField('grossProfitLoss', v)}
                salesBase={salesBase}
                kind="key"
            />

            <Section
                title="Indirect Expenses"
                accent="fr-expense"
                items={draft.indirectExpenses}
                onChange={(items) => setField('indirectExpenses', items)}
                salesBase={salesBase}
            />
            <TotalRow
                label="Total Indirect Expenses"
                value={draft.totalIndirectExpenses}
                onChange={(v) => setField('totalIndirectExpenses', v)}
                salesBase={salesBase}
                kind="expense"
            />
            <TotalRow
                label="Total Expense"
                value={draft.totalExpense}
                onChange={(v) => setField('totalExpense', v)}
                salesBase={salesBase}
                kind="expense"
            />
            <TotalRow
                label="NET OPERATING PROFIT / LOSS"
                value={draft.netOperatingProfitLoss}
                onChange={(v) => setField('netOperatingProfitLoss', v)}
                salesBase={salesBase}
                kind="key"
            />

            <Section
                title="Indirect Income"
                accent="fr-income"
                items={draft.indirectIncome}
                onChange={(items) => setField('indirectIncome', items)}
                salesBase={salesBase}
            />
            <TotalRow
                label="Total Indirect Income"
                value={draft.totalIndirectIncome}
                onChange={(v) => setField('totalIndirectIncome', v)}
                salesBase={salesBase}
                kind="income"
            />
            <TotalRow
                label="NET PROFIT / LOSS"
                value={draft.netProfitLoss}
                onChange={(v) => setField('netProfitLoss', v)}
                salesBase={salesBase}
                kind="key"
            />

            <div className="fr-recompute">
                <button type="button" className="fr-btn fr-btn-ghost" onClick={recomputeTotals}>
                    Recompute totals from line items
                </button>
                <small>Use this if you manually edit line items and want totals to refresh.</small>
            </div>
        </div>
    );
};

export default PnLEditor;
